import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Grid, Group, Loader, Paper, Stack, Text, ThemeIcon, UnstyledButton } from "@mantine/core";
import { IconChevronRight, IconMinus, IconPlus } from "@tabler/icons-react";

import { ReportSection } from "../components/ReportSection";

interface ReviewLike {
    pros?: Array<string | null> | null;
    cons?: Array<string | null> | null;
}

export interface ReportFeedbackProps {
    reviews?: Array<ReviewLike | null> | null;
    isLoading?: boolean;
    // When true (during PDF export), every collapsible group is rendered expanded so the detail
    // is captured. A closed <Collapse> renders nothing, so this must be set before the capture.
    forceExpand?: boolean;
}

interface FeedbackItem {
    text: string;
    count: number;
}

interface FeedbackGroupData {
    key: string;
    label: string;
    total: number;
    items: FeedbackItem[];
    single: boolean;
}

// Short Czech function words that shouldn't anchor a keyword group.
const STOPWORDS = new Set([
    "jsou",
    "ale",
    "jako",
    "nebo",
    "moc",
    "velmi",
    "tak",
    "jen",
    "pro",
    "jsem",
    "jste",
    "byl",
    "byla",
    "bylo",
    "mit",
    "byt",
    "ten",
    "tato",
    "toto",
    "tam",
    "kde",
    "kdy",
    "vse",
    "neni",
    "nic",
    "the",
    "and",
]);

// Lowercase + strip diacritics, so "Vůně" / "vune" / "VŮNĚ" collapse to one keyword.
const normalize = (value: string) =>
    value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

// Split into words, keep each word's original (diacritic) form alongside its normalized key.
const keywordsOf = (text: string): Array<{ key: string; original: string }> => {
    const words = text.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
    const seen = new Map<string, string>();
    words.forEach((word) => {
        const key = normalize(word);
        if (key.length >= 3 && !STOPWORDS.has(key) && !seen.has(key)) {
            seen.set(key, word);
        }
    });
    return [...seen.entries()].map(([key, original]) => ({ key, original }));
};

// Tally pros/cons, then group items under their most-frequent shared keyword so near-duplicate
// phrasings ("Vůně", "Krásný vůně", "Příjemná vůně…") collapse under one heading.
const buildGroups = (
    reviews: Array<ReviewLike | null> | null | undefined,
    field: "pros" | "cons"
): FeedbackGroupData[] => {
    const itemCounts = new Map<string, number>();
    (reviews || []).forEach((review) => {
        (review?.[field] || []).forEach((value) => {
            const text = value?.trim();
            if (text) {
                itemCounts.set(text, (itemCounts.get(text) || 0) + 1);
            }
        });
    });
    const items = [...itemCounts.entries()].map(([text, count]) => ({ text, count }));

    const freq = new Map<string, number>();
    const original = new Map<string, string>();
    const itemKeys = new Map<string, string[]>();
    items.forEach(({ text, count }) => {
        const kws = keywordsOf(text);
        itemKeys.set(
            text,
            kws.map((kw) => kw.key)
        );
        kws.forEach(({ key, original: word }) => {
            freq.set(key, (freq.get(key) || 0) + count);
            if (!original.has(key)) {
                original.set(key, word);
            }
        });
    });

    const groups = new Map<string, FeedbackItem[]>();
    items.forEach(({ text, count }) => {
        const keys = itemKeys.get(text) || [];
        // assign to the highest-frequency keyword (tie → longest, for a more specific anchor)
        const anchor = keys.reduce<string | null>((best, key) => {
            if (best === null) {
                return key;
            }
            const f = freq.get(key) || 0;
            const bf = freq.get(best) || 0;
            return f > bf || (f === bf && key.length > best.length) ? key : best;
        }, null);
        const groupKey = anchor ?? `text:${text}`;
        if (!groups.has(groupKey)) {
            groups.set(groupKey, []);
        }
        groups.get(groupKey)?.push({ text, count });
    });

    return [...groups.entries()]
        .map(([groupKey, groupItems]) => {
            const sorted = [...groupItems].sort((a, b) => b.count - a.count);
            const total = sorted.reduce((sum, item) => sum + item.count, 0);
            const single = sorted.length === 1;
            const label = single ? sorted[0].text : capitalize(original.get(groupKey) || groupKey);
            return { key: groupKey, label, total, items: sorted, single };
        })
        .sort((a, b) => b.total - a.total)
        .slice(0, 8);
};

const Count = ({ value }: { value: number }) => (
    <Text size="sm" weight={700} color="#868e96" style={{ fontVariantNumeric: "tabular-nums", flex: "none" }}>
        {value}×
    </Text>
);

const FeedbackGroup = ({
    group,
    positive,
    forceOpen,
}: {
    group: FeedbackGroupData;
    positive: boolean;
    forceOpen?: boolean;
}) => {
    const [open, setOpen] = useState(false);
    const color = positive ? "teal" : "red";
    const expanded = open || Boolean(forceOpen);

    if (group.single) {
        return (
            <Group spacing={12} noWrap align="flex-start">
                <ThemeIcon size={28} radius="xl" variant="light" color={color}>
                    {positive ? <IconPlus size={16} /> : <IconMinus size={16} />}
                </ThemeIcon>
                <Text size="sm" color="#39435A" style={{ flex: 1 }}>
                    {group.label}
                </Text>
                <Count value={group.total} />
            </Group>
        );
    }

    const details = (
        <Stack spacing={10} mt={10} pl={40}>
            {group.items.map((item, index) => (
                <Group key={index} spacing={12} noWrap align="flex-start">
                    <Text size="sm" color="#39435A" style={{ flex: 1 }}>
                        {item.text}
                    </Text>
                    <Count value={item.count} />
                </Group>
            ))}
        </Stack>
    );

    return (
        <Box>
            <UnstyledButton onClick={() => setOpen((value) => !value)} style={{ width: "100%" }}>
                <Group spacing={12} noWrap align="center">
                    <ThemeIcon size={28} radius="xl" variant="light" color={color}>
                        {positive ? <IconPlus size={16} /> : <IconMinus size={16} />}
                    </ThemeIcon>
                    <Group spacing={6} noWrap style={{ flex: 1 }}>
                        <Text size="sm" weight={600} color="#0F1B3D">
                            {group.label}
                        </Text>
                        <IconChevronRight
                            size={15}
                            style={{
                                transform: expanded ? "rotate(90deg)" : "none",
                                transition: "transform 120ms ease",
                                color: "#adb5bd",
                            }}
                        />
                    </Group>
                    <Count value={group.total} />
                </Group>
            </UnstyledButton>
            {/* When printing, render the detail inline: a closed Collapse renders nothing and an
                opening one animates, so neither state would be captured in the PDF. */}
            {forceOpen ? details : <Collapse in={open}>{details}</Collapse>}
        </Box>
    );
};

const FeedbackCard = ({
    title,
    groups,
    positive,
    forceOpen,
}: {
    title: string;
    groups: FeedbackGroupData[];
    positive: boolean;
    forceOpen?: boolean;
}) => {
    const { t } = useTranslation(["common"]);
    return (
        <Paper withBorder radius={16} p={24} style={{ height: "100%" }}>
            <Text mb={20} weight={700} color="#0F1B3D">
                {title}
            </Text>
            {groups.length === 0 ? (
                <Text size="sm" color="dimmed">
                    {t("campaign_report_no_data")}
                </Text>
            ) : (
                <Stack spacing={16}>
                    {groups.map((group) => (
                        <FeedbackGroup key={group.key} group={group} positive={positive} forceOpen={forceOpen} />
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

export const ReportFeedback = ({ reviews, isLoading, forceExpand }: ReportFeedbackProps) => {
    const { t } = useTranslation(["common"]);
    const likes = useMemo(() => buildGroups(reviews, "pros"), [reviews]);
    const dislikes = useMemo(() => buildGroups(reviews, "cons"), [reviews]);
    // Expand every collapsible group during PDF export, so the capture shows the full detail.
    const forceOpen = forceExpand;

    return (
        <ReportSection title={t("campaign_report_feedback")}>
            {isLoading ? (
                <Paper withBorder radius={16} p={24}>
                    <Loader size="sm" />
                </Paper>
            ) : (
                <Grid>
                    <Grid.Col md={6}>
                        <FeedbackCard
                            title={t("campaign_report_feedback_likes")}
                            groups={likes}
                            positive
                            forceOpen={forceOpen}
                        />
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <FeedbackCard
                            title={t("campaign_report_feedback_dislikes")}
                            groups={dislikes}
                            positive={false}
                            forceOpen={forceOpen}
                        />
                    </Grid.Col>
                </Grid>
            )}
        </ReportSection>
    );
};
